import { useCallback, useEffect, useMemo } from 'react';
import { useMatches, useNavigate, useSearchParams } from 'react-router-dom';
import { type RegistrationRouteName } from '@/registration/pages';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from './useRegisterRoute';
import { type RegistrationSteps, RegistrationStrategyType } from '@/api/registration';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';

export interface RegisterStep {
  routeName: RegistrationRouteName
  isEnable?: boolean
}

export function useRegisterSteps() {
  const { tenantCode, eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const steps = useMemo(
    () => { return registrationQuery?.data?.registrationSteps ?? []; },
    [registrationQuery?.data?.registrationSteps]
  );

  const matches = useMatches();
  const currentStepRouteName = useMemo(
    () => { return matches[matches.length - 1].id as RegistrationRouteName; }
    , [matches]);

  const stepIndex = useMemo(
    () => { return steps.findIndex((currentStep) => { return currentStep.routeName === currentStepRouteName; }); }
    , [currentStepRouteName, steps]);

  const currentStep = useMemo(() => { return steps[stepIndex]; }, [stepIndex, steps]);

  const isReviewing = useMemo(() => { return !!searchParams.get('review'); }, [searchParams]);

  const findNextStep = useCallback(() => {
    const nextStep = steps[stepIndex + 1];
    return nextStep;
  }, [stepIndex, steps]);

  const findPreviousStep = useCallback(() => {
    if (stepIndex === -1) return null;

    const prevStep = steps[stepIndex - 1];
    return prevStep;
  }, [stepIndex, steps]);

  const toNextStep = useCallback(({ additionalParams } = {} as any) => {
    if (isReviewing) {
      searchParams.delete('review');
      return navigate(generatePageUrl('RegistrationRegisterReview', { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
    const nextStep = findNextStep();
    if (nextStep) {
      let params = Object.fromEntries(searchParams);
      if (additionalParams) {
        params = { ...params, ...additionalParams };
      }
      navigate(generatePageUrl(nextStep.routeName, { tenantCode, eventId }, params));
    }
  }, [eventId, findNextStep, isReviewing, navigate, searchParams, tenantCode]);

  // TODO: toNextStep not use latest data, need to fix and remove toNextStepWithSteps
  const toNextStepWithSteps = useCallback((registrationSteps: RegistrationSteps) => {
    if (isReviewing) {
      searchParams.delete('review');
      return navigate(generatePageUrl('RegistrationRegisterReview', { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }

    const nextStep = registrationSteps[stepIndex + 1];
    if (nextStep) {
      navigate(generatePageUrl(nextStep.routeName, { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
  }, [eventId, isReviewing, navigate, searchParams, stepIndex, tenantCode]);

  const generateNextStepUrl = useCallback((additionalParams: object) => {
    const nextStep = findNextStep();
    const params = { ...Object.fromEntries(searchParams), ...additionalParams };
    return generatePageUrl(nextStep.routeName, { tenantCode, eventId }, params);
  }, [eventId, findNextStep, searchParams, tenantCode]);

  const generatePaymentProcessingStepUrl = useCallback((additionalParams: object) => {
    const nextStep = {
      routeName: 'RegistrationRegisterPaymentProcessing' as RegistrationRouteName,
      isEnable: true,
    };
    const params = { ...Object.fromEntries(searchParams), ...additionalParams };
    return generatePageUrl(nextStep.routeName, { tenantCode, eventId }, params);
  }, [eventId, searchParams, tenantCode]);

  const toPaymentProcessingStep = useCallback(
    (additionalParams: object) => {
      const nextStep = generatePaymentProcessingStepUrl(additionalParams);
      navigate(nextStep);
    },
    [generatePaymentProcessingStepUrl, navigate]
  );

  const toPreviousStep = useCallback(() => {
    if (isReviewing) {
      searchParams.delete('review');
      return navigate(generatePageUrl('RegistrationRegisterReview', { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
    const previousStep = findPreviousStep();
    if (previousStep) {
      navigate(generatePageUrl(previousStep.routeName, { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
  }, [eventId, findPreviousStep, isReviewing, navigate, searchParams, tenantCode]);

  const displayShoppingCart = useMemo(() => {
    if (currentStep) {
      if (currentStep.routeName === 'RegistrationRegisterPersonalInfo' ||
        currentStep.routeName === 'RegistrationRegisterAdditionalInfo' ||
        currentStep.routeName === 'RegistrationRegisterReview') {
        if (registrationQuery?.data?.registrationStrategy === RegistrationStrategyType.FORMS_FIRST) {
          return true;
        } else if (registrationQuery?.data?.registrationStrategy === RegistrationStrategyType.PAYMENT_FIRST) {
          return false;
        }
      }
      if (currentStep.routeName === 'RegistrationRegisterConfirmation') {
        return false;
      }
      return true;
    }
  }, [registrationQuery?.data?.registrationStrategy, currentStep]);

  return {
    steps,
    currentStep,
    isReviewing,
    findNextStep,
    findPreviousStep,
    toNextStep,
    toNextStepWithSteps,
    generateNextStepUrl,
    generatePaymentProcessingStepUrl,
    toPaymentProcessingStep,
    toPreviousStep,
    displayShoppingCart,
    stepIndex,
  };
}
